import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Platform } from '@ionic/angular';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AttachmentWorkItem } from 'src/app/business/attachment/attachment-work-item';
import { IDispatcher } from 'src/app/common/contracts/dispatch/dispatcher';
import { Dispatcher } from 'src/app/common/dispatch/dispatcher';
import { Logger } from 'src/app/common/logging/logger';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';
import { AttachmentMetadata } from 'src/app/shared/models/attachment/attachment-meta-data.model';
import { AttachmentDatabaseService } from 'src/app/shared/services/attachment/attachment-database.service';
import { AttachmentQueuesProcessorService } from 'src/app/shared/services/attachment/attachment-queues-processor.service';
import {
  AttachmentTransferService,
  OfflineUploadResult,
  UploadResult,
} from 'src/app/shared/services/attachment/attachment-transfer.service';
import { Database } from 'src/app/shared/services/attachment/Database';

import { AttachmentModelName } from '../../models/model-names';
import { AuthService } from '../auth.service';
import { ConnectionStateService } from '../connection-state.service';
import { ATTACHMENT_LOCAL_PREFIX, IAttachmentService } from './attachment-service.interface';
import { AttachmentUtilsService } from './attachment-utils.service';
export type UploadReadyMetadata = Omit<AttachmentMetadata & { uniqueId: string }, 'name'>;
/** name: Temporary filename used only to extract the file extension; removed before upload */
export type CreateMetadata = Partial<AttachmentMetadata> & { name: string };
type CreateObjectUrlType = (object: any) => string;

export const CreateObjectUrl = new InjectionToken<CreateObjectUrlType>('createObjectUrl', {
  providedIn: 'root',
  factory: () => URL.createObjectURL,
});

@Injectable({ providedIn: 'root' })
export class AttachmentService implements IAttachmentService {
  static TAG = 'AttachmentService';

  private _websocketConnectedSubscription = new Subscription();
  private _isBusyWithOfflineUpload: boolean;

  public get offlineTxSuccess(): Observable<OfflineUploadResult> {
    return this._attachmentTransferService.offlineTxSuccess;
  }

  constructor(
    private _auth: AuthService,
    private _platform: Platform,
    private _connectionStateService: ConnectionStateService,
    private _attachmentDbProvider: AttachmentDatabaseService,
    private _attachmentTransferService: AttachmentTransferService,
    private _attachmentQueuesProcessor: AttachmentQueuesProcessorService,
    private _attachmentUtils: AttachmentUtilsService,
    private _logger: Logger,
    private _attachmentWorkItem: AttachmentWorkItem,
    @Inject(Dispatcher) private _dispatcher: IDispatcher<AttachmentDto>
  ) {}

  public async init() {
    this._logger.info('[AttachmentService] Initializing service');
    this._platform.pause.subscribe(_ => {
      if (this._websocketConnectedSubscription && !this._websocketConnectedSubscription.closed) {
        this._websocketConnectedSubscription.unsubscribe();
      }
    });
    this._platform.resume.subscribe(_ => this.registerOnlineObservable());
    this.registerOnlineObservable();
    this.performDelete = this.performDelete.bind(this);
    this.uploadLocal = this.uploadLocal.bind(this);
  }

  public async save(attachments: AttachmentDto[]) {
    await this._platform.ready();
    if (this._platform.is('hybrid') || !attachments || !attachments.length) {
      return;
    }

    await Promise.all(attachments.map(attachment => this._attachmentWorkItem.create(attachment)));

    this._dispatcher.sync(AttachmentModelName, {
      created: attachments,
      updated: [],
      deleted: [],
    });
  }

  public async get<T extends 'blobUrl' | 'blob' | 'base64'>(
    id: string,
    returnValue: T
  ): Promise<T extends 'blob' ? Blob : string> {
    let blob = await this.getLocalBlob(id);
    if (!blob) {
      const metadata = await this.getAttachmentDto(id);
      blob = await this.download(id, metadata.contentType);
    }

    return this._attachmentUtils.transformBlob(blob, returnValue) as Promise<T extends 'blob' ? Blob : string>;
  }

  private async getLocalBlob(id: string) {
    const blob = (await this._attachmentDbProvider.get(Database.Blob, id)) as Blob;
    if (!blob && id.startsWith(ATTACHMENT_LOCAL_PREFIX)) {
      throw Error(`blob is missing for ${id}`);
    }

    return blob;
  }

  public async download(id: string, mimeType: string): Promise<Blob> {
    await this._auth.init;
    const blob = await this._attachmentTransferService.fetchBlob(id, mimeType);
    return blob;
  }

  public async create(base64String: string, metadata: CreateMetadata): Promise<UploadResult> {
    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment`,
      type: 'debug',
      data: {
        base64size: base64String?.length,
        metadata: metadata,
      },
    });

    if (!base64String) {
      return Promise.reject('no image data');
    }

    const { blob, uploadMetadata, filename, mime } = await this._attachmentUtils.prepareAttachmentData(
      base64String,
      metadata
    );

    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment writeBlobToStorage`,
      type: 'debug',
      data: {
        blobSize: blob?.size,
      },
    });

    // Writing to storage
    await this._attachmentDbProvider.writeBlobToStorage(uploadMetadata.uniqueId, blob);
    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment writeAttachmentToStorage`,
      type: 'debug',
    });
    await this._attachmentDbProvider.writeAttachmentToStorage({
      _id: uploadMetadata.uniqueId,
      filename,
      contentType: mime,
      mime,
      uploadDate: new Date(new Date().toUTCString()),
      metadata: {
        ...uploadMetadata,
        createdAt: new Date(new Date().toUTCString()),
        createdBy: await this._auth.getCurrentUserId(),
      },
    });

    this._logger.addBreadcrumb({
      message: `${AttachmentService.TAG} addAttachment upload`,
      type: 'debug',
    });

    // Uploading the attachment
    return this._attachmentTransferService.upload(
      { blob, metadata: uploadMetadata, filename, mime },
      uploadMetadata.uniqueId,
      this._connectionStateService.isConnected
    );
  }

  public async uploadLocal(localId: string): Promise<UploadResult | null> {
    this._logger.info(`upload local ${localId}`);
    const isAlreadyUploaded = localId.startsWith(ATTACHMENT_LOCAL_PREFIX) === false;
    if (isAlreadyUploaded) {
      return null;
    }
    const [blob, attachment] = await Promise.all([
      this._attachmentDbProvider.get(Database.Blob, localId),
      this._attachmentDbProvider.get(Database.Attachment, localId),
    ]);

    if (!blob || !attachment) {
      if (blob) {
        this._logger.error(
          'local attachment found without blob - deleting it',
          new Error('local attachment found without blob - deleting it')
        );
        await this._attachmentDbProvider.remove(Database.Attachment, localId);
      }
      return null;
    }
    const { metadata } = attachment;
    this._logger.info(`upload local ${localId} - uploading`);
    return this._attachmentTransferService.upload(
      { blob, metadata, filename: attachment.filename, mime: attachment.mime },
      localId,
      this._connectionStateService.isConnected
    );
  }

  public async upload(localId: string): Promise<UploadResult | null> {
    return await this.uploadLocal(localId);
  }

  public async update(attachment: AttachmentDto): Promise<boolean> {
    await this._attachmentDbProvider.writeAttachmentToStorage({
      ...attachment,
      uploadDate: new Date(new Date().toUTCString()),
      metadata: { ...attachment.metadata, updatedBy: await this._auth.getCurrentUserId() },
    });

    await this._attachmentDbProvider.set(Database.AttachmentToUpdate, attachment._id, attachment);
    if (this._connectionStateService.isConnected) {
      await this.runAttachmentQueuesProcessor();
    }
    return true;
  }

  public async softDelete(attachment: AttachmentDto): Promise<boolean> {
    attachment.metadata.archived = true;

    await this._attachmentDbProvider.writeAttachmentToStorage({
      ...attachment,
      uploadDate: new Date(new Date().toUTCString()),
      metadata: { ...attachment.metadata, updatedBy: await this._auth.getCurrentUserId() },
    });

    await this._attachmentDbProvider.set(Database.AttachmentToUpdate, attachment._id, attachment);
    if (this._connectionStateService.isConnected) {
      await this.runAttachmentQueuesProcessor();
    }
    return true;
  }

  public async delete(id: string): Promise<void> {
    if (!this._connectionStateService.isConnected) {
      await this._attachmentDbProvider.set(Database.AttachmentToRemove, id, undefined);
      return;
    }
    try {
      await this.performDelete(id);
    } catch (error) {
      this._logger.error('delete image failed', error);
      await this._attachmentDbProvider.set(Database.AttachmentToRemove, id, undefined);
    }
  }

  private async performDelete(id: string): Promise<void> {
    this._logger.info(`[AttachmentService] Deleting attachment ${id}`);
    const isRemoteAttachment = id.startsWith(ATTACHMENT_LOCAL_PREFIX) === false;
    if (isRemoteAttachment) {
      this._logger.info(`[AttachmentService] Deleting remote attachment ${id}`);
      await this._attachmentTransferService.delete(id);
    }
    await this._attachmentDbProvider.purge(id);
    this._logger.info(`[AttachmentService] Deleted attachment ${id}`);
  }

  private async performUpdate(attachment: AttachmentDto): Promise<void> {
    this._logger.info(`[AttachmentService] Updating attachment ${attachment._id}`);
    const uploadedAttachment = await this._attachmentTransferService.update(attachment._id, attachment);
    await this._attachmentDbProvider.writeAttachmentToStorage(uploadedAttachment);
    await this._attachmentDbProvider.remove(Database.AttachmentToUpdate, attachment._id);
    this._logger.info(`[AttachmentService] Updated attachment ${attachment._id}`);
  }

  public async getAttachmentDto(id: string): Promise<AttachmentDto> {
    const attachment = this._attachmentDbProvider.getAttachmentDto(id);
    if (!attachment) {
      throw Error(`attachment is undefined for ${id}`);
    }
    return attachment;
  }

  public async isAttachmentBlobLocallyAvailable(id: string): Promise<boolean> {
    return this._attachmentDbProvider.isAttachmentBlobAvailable(id);
  }

  public async getLocalAttachments(): Promise<string[]> {
    return this._attachmentDbProvider.getLocalAttachments();
  }

  private registerOnlineObservable(): void {
    this._logger.info('[AttachmentService] Registering online observable');
    try {
      if (this._websocketConnectedSubscription != null && !this._websocketConnectedSubscription.closed) {
        this._websocketConnectedSubscription.unsubscribe();
      }
    } catch (e) {}
    this._websocketConnectedSubscription = combineLatest([
      this._connectionStateService.connectionState,
      this._auth.authenticatedEventPublisher,
    ])
      .pipe(
        map(([connectionStatus, authEvent]) => ({ isOnline: connectionStatus.isOnline, authEvent })),
        distinctUntilChanged(
          (x, y) => x.authEvent.isAuthenticated === y.authEvent.isAuthenticated && x.isOnline === y.isOnline
        ),
        filter(value => value.isOnline && value.authEvent.isAuthenticated && !this._isBusyWithOfflineUpload),
        debounceTime(2 * 1000), // avoid flickering online -> offline -> online,
        switchMap(async _ => {
          this._isBusyWithOfflineUpload = true;
          try {
            await this.runAttachmentQueuesProcessor();
          } catch (error) {
            this._logger.error('Error on websocketConnectedSubscription', error);
          } finally {
            this._isBusyWithOfflineUpload = false;
          }
        })
      )
      .subscribe(() => {});
  }

  public async runAttachmentQueuesProcessor() {
    const runId = Math.floor(Math.random() * (10000000 - 1 + 1)) + 1;
    this._logger.info(`[AttachmentService-${runId}] Attachment queues processor started`);
    await this._attachmentQueuesProcessor.processQueues(
      this.performDelete.bind(this),
      this.uploadLocal.bind(this),
      this.performUpdate.bind(this)
    );
    this._logger.info(`[AttachmentService-${runId}] Attachment queues processor done`);
  }
}
